import { useMemo } from 'react';
import { useTheme } from '@mui/material/styles';

import useGameTypeMetadataQuery from './useGameTypeMetadataQuery';
import useSurfaceRenderScale from './useSurfaceRenderScale';
import { useCurrentGame } from '@/features/games/context/CurrentGameContext';

/**
 * Computed pixel height of the playing-surface visualization (e.g. hockey rink)
 * for the current game's type metadata. Returns 0 while data is loading or when
 * the underlying dimensions aren't yet known.
 *
 * Used by:
 * - GamePositionsRepresentation — to size the rink and position entities.
 * - GamePageContents — to set the autoIso Panel's minSize so the rink can't be
 *   resized smaller than its content needs.
 */
const useGameTypeMetadataHeight = (): number => {
  const theme = useTheme();
  const { surfaceWidth, surfaceHeight } = useCurrentGame();
  const surfaceRenderScale = useSurfaceRenderScale();
  const gameTypeMetadataQuery = useGameTypeMetadataQuery();

  const aspectRatio = useMemo(() => {
    if (!surfaceWidth || !surfaceHeight || surfaceHeight === 0) return 1;
    return surfaceWidth / surfaceHeight;
  }, [surfaceWidth, surfaceHeight]);

  const gameTypeMetadataWidth = useMemo(
    () => surfaceWidth * surfaceRenderScale - Number(theme.spacing(6).slice(0, -2)),
    [surfaceWidth, surfaceRenderScale, theme],
  );

  return useMemo(() => {
    if (!gameTypeMetadataQuery.data || !gameTypeMetadataWidth || !aspectRatio) return 0;
    const height = gameTypeMetadataWidth / aspectRatio;
    return Number.isFinite(height) ? height : 0;
  }, [gameTypeMetadataWidth, aspectRatio, gameTypeMetadataQuery.data]);
};

export default useGameTypeMetadataHeight;
